import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { FiLock, FiArrowLeft, FiLoader, FiSave, FiEye, FiEyeOff } from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import Logo from '../components/common/Logo'

function ResetPassword() {
  const { updatePassword } = useAuth()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Add auth-page class for iOS safe area styling
  useEffect(() => {
    document.documentElement.classList.add('auth-page');
    document.body.classList.add('auth-page');
    return () => {
      document.documentElement.classList.remove('auth-page');
      document.body.classList.remove('auth-page');
    };
  }, []);
  const [sessionReady, setSessionReady] = useState(false)

  // Handle URL hash parameters and check for existing session
  useEffect(() => {
    const handleAuthState = async () => {
      try {
        // Check URL parameters to see if this is a password reset flow
        const urlParams = new URLSearchParams(window.location.search)
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const isPasswordReset = urlParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery'

        console.log('Password reset flow detected:', isPasswordReset)
        console.log('URL search params:', window.location.search)
        console.log('URL hash params:', window.location.hash)

        // Check if we already have a session (from ConfirmationURL redirect)
        const { data: existingSession } = await supabase.auth.getSession()

        if (existingSession.session) {
          console.log('User already has valid session from email link')
          // For password reset, we want to show the form even if logged in
          setSessionReady(true)
          return
        }

        // If no session, try to extract tokens from URL hash (fallback)
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        const type = hashParams.get('type')

        console.log('URL hash params:', { accessToken: accessToken?.substring(0, 20) + '...', refreshToken: refreshToken?.substring(0, 20) + '...', type })

        if (accessToken && type === 'recovery') {
          console.log('Setting up password recovery session...')

          // Set the session in Supabase
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || accessToken
          })

          if (error) {
            console.error('Error setting session:', error)
            setError(`Session setup failed: ${error.message}. Please request a new password reset link.`)
          } else {
            console.log('Session set successfully:', data.session ? 'Session exists' : 'No session data')

            // Verify the session was set
            const { data: currentSession } = await supabase.auth.getSession()
            console.log('Current session after setup:', currentSession.session ? 'Valid session' : 'No session found')

            if (currentSession.session) {
              setSessionReady(true)
            } else {
              setError('Failed to establish session. Please try the reset link again.')
            }
          }
        } else {
          console.log('No valid session or URL parameters found')
          setError('Please use the reset password link from your email.')
        }
      } catch (err) {
        console.error('Error handling auth state:', err)
        setError('Something went wrong. Please try again.')
      }
    }

    handleAuthState()
  }, [])

  // Redirect to login after successful reset
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        navigate('/login', { replace: true })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [success, navigate])

  const validatePasswords = () => {
    if (!password) {
      setError('Please enter a new password')
      return false
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long')
      return false
    }

    if (!confirmPassword) {
      setError('Please confirm your password')
      return false
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!sessionReady) {
      setError('Session not ready. Please wait or try again.')
      return
    }

    if (!validatePasswords()) {
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      // Double-check we have a valid session before attempting password update
      const { data: currentSession } = await supabase.auth.getSession()
      console.log('Session check before password update:', currentSession.session ? 'Valid' : 'Missing')

      if (!currentSession.session) {
        setError('Session expired. Please request a new password reset link.')
        setLoading(false)
        return
      }

      const { error } = await updatePassword(password)

      if (error) {
        console.error('Password update error:', error)
        setError(error.message || 'Failed to update password. Please try again.')
      } else {
        setSuccess('Password updated successfully! Redirecting to login...')
      }
    } catch (err) {
      console.error('Unexpected error during password update:', err)
      setError('An unexpected error occurred. Please try again.')
    }

    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-app flex items-center justify-center safe-area-inset-top">
      <Helmet>
        <title>Set New Password - TeeSync</title>
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full mx-4"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
            className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center shadow-card mx-auto mb-4"
          >
            <Logo size="lg" variant="white" />
          </motion.div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Set New Password</h1>
          <p className="text-text-secondary">Enter your new password below</p>
        </div>

        {/* Reset Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-surface rounded-xl border border-border-subtle shadow-card p-8"
        >
          {!sessionReady && !error && (
            <div className="text-center py-8">
              <SafeIcon icon={FiLoader} className="w-8 h-8 animate-spin mx-auto mb-4 text-accent" />
              <p className="text-text-secondary">Setting up your password reset session...</p>
            </div>
          )}

          {(error && !sessionReady) && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-scarlet-50 border border-scarlet-200 text-scarlet-700 px-4 py-3 rounded-lg text-sm"
            >
              {error}
            </motion.div>
          )}

          {sessionReady && (
            <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-scarlet-50 border border-scarlet-200 text-scarlet-700 px-4 py-3 rounded-lg text-sm"
              >
                {error}
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-accent/10 border border-accent text-accent px-4 py-3 rounded-lg text-sm"
              >
                {success}
              </motion.div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-secondary mb-2">
                New Password
              </label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-5 h-5" />
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-border-subtle rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-app text-text-primary placeholder-text-muted text-base font-sans"
                  placeholder="Enter new password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-secondary"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-secondary mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-5 h-5" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-border-subtle rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-app text-text-primary placeholder-text-muted text-base font-sans"
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-secondary"
                >
                  <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || success}
              className="w-full flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-accent/90 focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:bg-accent/50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? (
                <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Update Password
                  <SafeIcon icon={FiSave} className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-accent hover:text-accent font-medium"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
              Back to Sign In
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default ResetPassword
